<template>
  <div>
    <div class="card-container" v-if="data">
      <div class="scroller">
        <loading-component :value="loading" />
        <order-card :id="parseInt($route.params.id)" :data="data" />
        <logs-table
          :logs="filteredStatusesLogs"
          :color-coding="actionsColorCoding"
          title-key="status"
          color-coding-key="value"
          datetime-key="created_at"
          v-if="filteredStatusesLogs.length"
          @status-click="focusOnStatus"
        />
        
        <!-- Unpaid ride (operational entries doc 2026-10-03, Go-Live
             automatic entry 4): a DONE booking nobody collected. -->
        <v-card class="mb-3" v-if="data.status === 'DONE' && hasPermission('MANAGE_FINANCE_JOURNALS')">
          <v-card-title>
            <span>{{ $t("ridePage.unpaidRideTitle") }}</span>
          </v-card-title>
          <v-card-text>
            <p class="grey--text mb-2">{{ $t("ridePage.unpaidRideHint") }}</p>
            <v-btn color="warning" small :loading="settlingUnpaid" @click="unpaidDialog = true">
              {{ $t("ridePage.settleUnpaid") }}
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Booking Complaints Section -->
        <v-card class="complaints-section" v-if="data">
          <v-card-title class="complaints-header">
            <span>{{ $t("ridePage.complaints") }}</span>
            <v-spacer></v-spacer>
            <v-chip small color="warning" v-if="pendingComplaintsCount > 0">
              {{ $t("ridePage.pendingChip", { count: pendingComplaintsCount }) }}
            </v-chip>
          </v-card-title>

          <v-card-text>
            <!-- Add New Complaint -->
            <div class="add-complaint-form mb-4">
              <v-textarea
                v-model="newComplaintDescription"
                :label="$t('ridePage.addComplaintLabel')"
                :placeholder="$t('ridePage.complaintPlaceholder')"
                rows="3"
                outlined
                dense
                hide-details
              ></v-textarea>
              <v-btn
                color="primary"
                small
                @click="createComplaint"
                :loading="creatingComplaint"
                :disabled="!newComplaintDescription || newComplaintDescription.trim() === ''"
                class="mt-2"
              >
                {{ $t("ridePage.addComplaint") }}
              </v-btn>
            </div>
            
            <!-- Complaints List -->
            <div v-if="complaints.length > 0" class="complaints-list">
              <v-card
                v-for="complaint in complaints"
                :key="complaint.id"
                class="complaint-item mb-3"
                outlined
              >
                <v-card-text>
                  <div class="complaint-header-row">
                    <v-chip
                      :color="complaint.status === 'PENDING' ? 'warning' : 'success'"
                      small
                      text-color="white"
                    >
                      {{ complaintStatusLabel(complaint.status) }}
                    </v-chip>
                    <span class="complaint-date">
                      {{ $t("ridePage.createdAt") }} {{ formatDate(complaint.created_at) }}
                      <span v-if="complaint.creator">
                        {{ $t("ridePage.by") }} {{ complaint.creator.first_name }} {{ complaint.creator.last_name }}
                      </span>
                    </span>
                  </div>

                  <p class="complaint-description mt-2 mb-2">{{ complaint.description }}</p>

                  <!-- Resolved Info -->
                  <div v-if="complaint.status === 'RESOLVED'" class="resolved-info">
                    <div class="resolved-date mb-1">
                      <strong>{{ $t("ridePage.resolvedAt") }}</strong> {{ formatDate(complaint.resolved_at) }}
                      <span v-if="complaint.resolver">
                        {{ $t("ridePage.by") }} {{ complaint.resolver.first_name }} {{ complaint.resolver.last_name }}
                      </span>
                    </div>
                    <div v-if="complaint.resolution_comment" class="resolution-comment">
                      <strong>{{ $t("ridePage.resolutionLabel") }}</strong> {{ complaint.resolution_comment }}
                    </div>
                  </div>

                  <!-- Resolve Button for Pending -->
                  <v-btn
                    v-if="complaint.status === 'PENDING'"
                    color="success"
                    small
                    @click="openResolveDialog(complaint)"
                    class="mt-2"
                  >
                    {{ $t("ridePage.resolve") }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </div>

            <div v-else class="no-complaints text-center py-4">
              <p class="grey--text">{{ $t("ridePage.noComplaints") }}</p>
            </div>
          </v-card-text>
        </v-card>

        <!-- In-app conversation + call log (audit) -->
        <ride-conversation v-if="data" :booking-id="parseInt($route.params.id)" />

        <!-- Accountant doc 2026-10-03 item 1.5: automatic compensation of the
             driver for a cancelled ride (or why none was paid). -->
        <cancellation-compensation-panel
          v-if="data && isCancelledStatus"
          :booking-id="parseInt($route.params.id)"
          :status="data.status"
        />

        <!-- Complaints and category section commented as requested
        <div class="complaints-container">
          <v-select
            label="Select"
            v-model="complaintSelectedVal"
            item-text="name"
            item-value="id"
            :items="complaintCategories"
            placeholder="Select a category"
          ></v-select>
          <button class="complaint-button" @click="saveCategory()">Save</button>
        </div>
        <logs-table
          :logs="data.all_complaints"
          title-key="description"
          datetime-key="created_at"
          doer-key="doer"
          captain-key="entity_id"
          :complaintStatus="complaintStatus"
          :addButton="true"
          @added="addComplaint"
          @showDetails="showDetails"
          @changeComplaintStatus="changeComplaintStatus"
        />
        -->
      </div>
    </div>
    <GmapMap :center="center" :options="options" :zoom="zoom" ref="map" class="map" v-if="data">
      <!-- Actual driven route (trip phase, passenger on board) -->
      <GmapPolyline
        v-if="path.length"
        :options="{ strokeColor: '#1C6758', strokeOpacity: 1.0, strokeWeight: 4 }"
        :path="path"
      ></GmapPolyline>
      <!-- Actual approach route (driver heading to pickup) -->
      <GmapPolyline
        v-if="approachPath.length"
        :options="{ strokeColor: '#2E86AB', strokeOpacity: 1.0, strokeWeight: 4 }"
        :path="approachPath"
      ></GmapPolyline>
      <!-- Estimated trip route from summaries.estimated_route (dashed) -->
      <GmapPolyline
        v-if="estimatedPath && estimatedPath.length"
        :options="dashedLineOptions('#333333')"
        :path="estimatedPath"
      ></GmapPolyline>
      <!-- Estimated approach route (driver -> pickup, dashed) -->
      <GmapPolyline
        v-if="captainEstimatedPath && captainEstimatedPath.length"
        :options="dashedLineOptions('#8E5BA6')"
        :path="captainEstimatedPath"
      ></GmapPolyline>
      <GmapMarker
        v-if="pickupPosition"
        :icon="{
        url: require('@/assets/map/pickup-pin.svg'),
        scaledSize: { width: 34, height: 42 },
      }"
        :position="pickupPosition"
        @mouseover="showByIndex = 'pickup'"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex == 'pickup'">
          <p style="margin-bottom: 0">{{ $t("ridePage.pickupLocation") }}</p>
        </gmap-info-window>
      </GmapMarker>
      <GmapMarker
        v-if="dropoffPosition"
        :icon="{
        url: require('@/assets/map/dropoff-pin.svg'),
        scaledSize: { width: 34, height: 42 },
      }"
        :position="dropoffPosition"
        @mouseover="showByIndex = 'dropoff'"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex == 'dropoff'">
          <p style="margin-bottom: 0">{{ $t("ridePage.dropoffLocation") }}</p>
        </gmap-info-window>
      </GmapMarker>
      <!-- Rider's real pickup point (vs the area-centroid pins); hidden while
           the customer is in the car (no live rider position exists) -->
      <GmapMarker
        v-if="passengerRealPosition && !passengerInCar"
        :icon="{
        url: require('@/assets/map/pickup-pin.svg'),
        scaledSize: { width: 24, height: 30 },
      }"
        :position="passengerRealPosition"
        @mouseover="showByIndex = 'passengerReal'"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex == 'passengerReal'">
          <p style="margin-bottom: 0">{{ $t("ridePage.passengerRealLocation") }}</p>
        </gmap-info-window>
      </GmapMarker>
      <GmapMarker
        v-for="(marker, index) in statusMarkers"
        :key="`status-marker-${marker.id || index}-${marker.status}`"
        :icon="{
          url: formSvg(actionsColorCoding[marker.value]),
          scaledSize: { width: 50, height: 35 },
        }"
        :position="getMarkerPositionWithOffset(marker, index)"
        @mouseover="showByIndex = index"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window :opened="showByIndex === index">
          <p style="margin-bottom: 0">
            {{ $t("ridePage.statusAtTime", {
              status: getStatusLabel(marker.value),
              time: $companyTime(marker.created_at, "HH[h]mm"),
            }) }}
          </p>
        </gmap-info-window>
      </GmapMarker>
      <!-- Live car. Hidden until a real fix exists (never parked at 0,0).
           Bound to the driverLocation object itself (stable reference, deep
           watched) so page re-renders do not re-issue setPosition; the
           between-ping tween drives the marker object directly. -->
      <GmapMarker
        v-if="startTracking && hasDriverPosition"
        ref="driverMarker"
        :icon="{
        url: require('@/assets/markers/FREE.png'),
        scaledSize: { width: 34, height: 42 },
      }"
        :position="driverLocation"
        :title="driverMarkerTitle"
        @mouseover="showByIndex = 'driver'"
        @mouseout="showByIndex = null"
      >
        <gmap-info-window v-if="driverMarkerTitle" :opened="showByIndex == 'driver'">
          <p style="margin-bottom: 0">{{ driverMarkerTitle }}</p>
        </gmap-info-window>
      </GmapMarker>
      <!-- Temporary highlight for the focused status event -->
      <GmapCircle
        v-if="highlightPos"
        :center="highlightPos"
        :radius="35"
        :options="{
          fillColor: '#E10015',
          fillOpacity: 0.25,
          strokeColor: '#E10015',
          strokeWeight: 2,
        }"
      />
    </GmapMap>
    <!-- Live feed state: the users socket died (namespace auth error, network);
         the 30 s snapshot poll keeps the car roughly current meanwhile -->
    <div class="ride-live-feed-banner" v-if="data && startTracking && liveFeedDisconnected">
      {{ $t("ridePage.liveFeedDisconnected") }}
    </div>
    <div class="map-overlays" v-if="data && (legendItems.length || startTracking)">
      <!-- Map layers legend (only layers that currently have data) -->
      <div class="map-legend" v-if="legendItems.length">
        <div class="legend-row" v-for="item in legendItems" :key="item.key">
          <span
            class="legend-swatch"
            :class="{ 'legend-swatch--dashed': item.dashed }"
            :style="item.dashed ? { borderTop: '3px dashed ' + item.color } : { backgroundColor: item.color }"
          ></span>
          <span class="legend-label">{{ item.label }}</span>
        </div>
      </div>
      <!-- Camera follow (pan only, never zoom). Default off: the map stays
           where the operator put it. -->
      <label class="map-follow-toggle" v-if="startTracking">
        <input type="checkbox" v-model="followDriver" />
        <span>{{ $t("ridePage.followDriver") }}</span>
      </label>
    </div>

    <!-- Resolve Complaint Dialog -->
    <v-dialog v-model="resolveDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("ridePage.resolveComplaintTitle") }}</span>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="resolutionComment"
            :label="$t('ridePage.resolutionCommentLabel')"
            :placeholder="$t('ridePage.resolutionPlaceholder')"
            rows="4"
            outlined
            auto-grow
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="resolveDialog = false" :disabled="resolvingComplaint">
            {{ $t("ridePage.cancel") }}
          </v-btn>
          <v-btn
            color="success"
            @click="resolveComplaint"
            :loading="resolvingComplaint"
          >
            {{ $t("ridePage.resolve") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unpaidDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("ridePage.settleUnpaidConfirmTitle") }}</span>
        </v-card-title>
        <v-card-text>{{ $t("ridePage.settleUnpaidConfirm") }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="unpaidDialog = false" :disabled="settlingUnpaid">
            {{ $t("ridePage.cancel") }}
          </v-btn>
          <v-btn color="warning" :loading="settlingUnpaid" @click="settleUnpaid">
            {{ $t("ridePage.settleUnpaid") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>

</template>
<script>
/* eslint-disable */

import OrderCard from "@/components/dispatching/OrderCard.vue";
import RideConversation from "@/components/dispatching/RideConversation.vue";
import CancellationCompensationPanel from "@/components/dispatching/CancellationCompensationPanel.vue";
import LogsTable from "@/components/dispatching/LogsTable.vue";
import AdditionalInfo from "@/components/dispatching/AdditionalInfo.vue";
import GmapPolyline from "vue2-google-maps/dist/components/polyline";
import socketEventsMixin from "@/mixins/socketEventsMixin";
import axios from "axios";
import api from "@/constants/api";
import LoadingComponent from "@/components/LoadingComponent";
import { formatCompanyTime } from "@/util/companyTime";
import { GOOGLE_MAP_OPTIONS } from "@/constants/mapOptions";

function hasValidCoordinates(geo) {
  if (!geo || !Array.isArray(geo.coordinates) || geo.coordinates.length < 2) return false;
  const [lng, lat] = geo.coordinates;
  return lng !== null && lng !== undefined && lat !== null && lat !== undefined;
}

function toLatLng(geo) {
  if (!hasValidCoordinates(geo)) return null;
  const [lng, lat] = geo.coordinates;
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
  return { lat: latNum, lng: lngNum };
}

// Car glide between two pings; kept below the realtime service's 2 s
// per-captain emit throttle so a tween always finishes before the next fix.
const DRIVER_TWEEN_MS = 900;
// REST re-sync of status / captain / last position while tracking: the
// safety net for a zombie socket (connected, no events) and for reconnects.
const LIVE_SNAPSHOT_MS = 30000;

// {
//   position: {
//     lat: 43.738359,
//     lng: 7.426723,
//   },
//   icon: {
//     url: require("@/assets/map/pickup-pin.svg"),
//     scaledSize: {
//       width: 34,
//       height: 42,
//     },
//   },
// },

function zoomUI(map, vm) {
  // Container

  const zoomUIContainer = document.createElement("div");
  zoomUIContainer.classList = "zoom-container";

  // Zoom in

  const zoomIn = document.createElement("button");
  zoomIn.classList = "zoom-button";
  zoomIn.title = vm.$t("ridePage.zoomIn");

  const zoomInText = document.createElement("div");
  zoomInText.innerHTML = `+`;
  zoomIn.appendChild(zoomInText);

  zoomUIContainer.append(zoomIn);

  zoomIn.addEventListener("click", () => {
    map.setZoom(map.getZoom() + 1);
  });

  // Zoom out

  const zoomOut = document.createElement("button");
  zoomOut.classList = "zoom-button";
  zoomOut.title = vm.$t("ridePage.zoomOut");

  const zoomOutText = document.createElement("div");
  zoomOutText.innerHTML = `-`;
  zoomOut.appendChild(zoomOutText);

  zoomUIContainer.append(zoomOut);

  zoomOut.addEventListener("click", () => {
    map.setZoom(map.getZoom() - 1);
  });

  map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomUIContainer);
}

export default {
  components: {
    OrderCard,
    RideConversation,
    CancellationCompensationPanel,
    GmapPolyline,
    LogsTable,
    AdditionalInfo,
    LoadingComponent,
  },
  title: "ridePage.pageTitle",
  mixins: [socketEventsMixin],
  events() {
    return {
      captain_location_updated: this.updateDriverLocation,
      [this.api.SOCKET.EVENTS.UPDATE_BOOKING_STATUS]: this.updateStatus,
      disconnect: this.onSocketDisconnect,
      connect: this.onSocketConnect,
      error: this.onSocketError,
      connect_error: this.onSocketError,
    };
  },
  watch: {
    followDriver(on) {
      // Immediate feedback instead of waiting up to a heartbeat for the next ping
      if (on && this.hasDriverPosition) this.panMapTo(this.driverLocation);
    },
  },
  computed: {
    isCancelledStatus() {
      return !!this.data && [
        "SYSTEM_CANCELLED",
        "PASSENGER_CANCELLED",
        "CAPTAIN_CANCELLED",
        "CC_CANCELLED",
      ].includes(this.data.status);
    },
    pickupPosition() {
      return this.data ? toLatLng(this.data.pick_up_location) : null;
    },
    dropoffPosition() {
      return this.data ? toLatLng(this.data.pull_down_location) : null;
    },
    passengerRealPosition() {
      return this.data ? toLatLng(this.data.passenger_location) : null;
    },
    legendItems() {
      const items = [];
      if (this.path.length)
        items.push({
          key: "trip",
          color: "#1C6758",
          dashed: false,
          label: this.$t("ridePage.legendTripRoute"),
        });
      if (this.approachPath.length)
        items.push({
          key: "approach",
          color: "#2E86AB",
          dashed: false,
          label: this.$t("ridePage.legendApproachRoute"),
        });
      if (this.estimatedPath.length)
        items.push({
          key: "tripEstimate",
          color: "#333333",
          dashed: true,
          label: this.$t("ridePage.legendTripEstimate"),
        });
      if (this.captainEstimatedPath.length)
        items.push({
          key: "approachEstimate",
          color: "#8E5BA6",
          dashed: true,
          label: this.$t("ridePage.legendApproachEstimate"),
        });
      return items;
    },
    statusMarkers() {
      if (!this.data || !Array.isArray(this.data.statuses)) return [];
      return this.data.statuses
        .filter((marker) => {
          if (!marker) return false;
          // Show ARRIVED, ACCEPTED and STARTED on map, hide only APPROVED, PAID
          if (["APPROVED", "PAID"].includes(marker.status))
            return false;
          return !!toLatLng(marker.location);
        })
        .map((marker) => {
          const position = toLatLng(marker.location);
          return {
            ...marker,
            _position: position,
            _originalPosition: position, // Keep original position
            value: marker.status, // Ensure value is set for color coding
          };
        });
    },
    startTracking() {
      if (!this.data) return false;
      return [
        // Only reservations (now=false) ever carry ACCEPTED; an instant
        // booking goes PENDING -> STARTED, so it is tracked from STARTED.
        !this.data.now && "ACCEPTED",
        "STARTED",
        "ARRIVED",
        "PICKED_UP",
      ].includes(this.data.status);
    },
    // Live polyline a socket point belongs to (same split as the realtime
    // service's phase tagging: approach until pickup, trip after).
    livePhase() {
      if (!this.data) return null;
      if (["ACCEPTED", "STARTED", "ARRIVED"].includes(this.data.status))
        return "approach";
      if (this.data.status === "PICKED_UP") return "trip";
      return null;
    },
    hasDriverPosition() {
      return !!(this.driverLocation.lat && this.driverLocation.lng);
    },
    passengerInCar() {
      return !!this.data && this.data.status === "PICKED_UP";
    },
    driverMarkerTitle() {
      if (this.passengerInCar) return this.$t("ridePage.customerInCar");
      if (!this.data) return "";
      return [this.data.captain_first_name, this.data.captain_last_name]
        .filter(Boolean)
        .join(" ");
    },

    filteredStatusesLogs() {
      if (!this.data || !this.data.statuses) return [];
      return this.data.statuses.map((status) => {
        const item = { ...status };
        item.value = item.status;
        item.status = this.getStatusLabel(item.status);
        return item;
      });
    },
    pendingComplaintsCount() {
      return this.complaints.filter(c => c.status === 'PENDING').length;
    },
  },
  data() {
    return {
      data: null,
      complaintSelectedVal: null,
      loading: null,
      complaintStatus: null,
      dialog: false,
      complaintCategories: [],
      // Damascus (service area); the one-shot fit on load replaces it
      center: { lat: 33.5138, lng: 36.2765 },
      zoom: 16,
      // Shared frozen constant (owner item 1.1, full-detail base map); never
      // rebuild it here or inline, the map deep-watches this prop.
      options: GOOGLE_MAP_OPTIONS,
      driverLocation: { lat: 0, lng: 0 },
      // Camera follows the car on each ping (pan only). Default off so the
      // map stays where the operator put it.
      followDriver: false,
      liveFeedDisconnected: false,
      // Actual driven route from RideTracking (trip phase)
      path: [],
      // Actual approach route from RideTracking (driver heading to pickup)
      approachPath: [],
      // Estimated route decoded from summaries.estimated_route
      estimatedPath: [],
      // Estimated approach route (driver -> pickup) from captain_passenger_estimated_route
      captainEstimatedPath: [],
      // Temporary highlight circle for the focused status event
      highlightPos: null,
      highlightTimeoutId: null,
      showByIndex: null,
      actionsColorCoding: {
        APPROVED: "#293462",
        ACCEPTED: "#293462",
        STARTED: "#1CD6CE",
        ARRIVED: "#FEDB39",
        AT_DESTINATION: "#1CD6CE",
        PICKED_UP: "#F57429",
        DONE: "#1C6758",
        PAID: "#1C6758",
        CAPTAIN_CANCELLED: "#9B1FE8",
        SYSTEM_CANCELLED: "#9B1FE8",
        PAX_CANCELLED: "#9B1FE8",
        CANCELLED: "#9B1FE8",
        REJECTED: "#E10015",
      },
      complaint: "",
      // Booking Complaints
      complaints: [],
      newComplaintDescription: "",
      creatingComplaint: false,
      resolveDialog: false,
      unpaidDialog: false,
      settlingUnpaid: false,
      selectedComplaint: null,
      resolutionComment: "",
      resolvingComplaint: false,
    };
  },
  methods: {
    changeComplaintStatus(value) {
      this.loading = true;
      let status = value;
      axios.put(`complaint/${this.$route.params.id}/update_status`, { status })
        .then(() => {
          this.loading = false;
          window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.saved"));

        }).catch((err) => {
          window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
          this.loading = false;
        });
    },

    onlyUnique(value, index, self) {
      self = self.map((item) => item.status);
      return (
        self.indexOf(value.status) === index &&
        !["APPROVED", "DONE", "STARTED", "ARRIVED"].includes(value.status)
      );
    },
    getStatusesToShow(allStatuses) {
      const statuses = {};

      allStatuses.forEach(item => {
        const { status, created_at } = item;
        if ((!["APPROVED", "DONE", "STARTED", "ARRIVED"].includes(status)) && (!(status in statuses) || created_at > statuses[status].created_at)) {
          statuses[status] = item;
        }
      });

      return Object.values(statuses);

    },
    getOrderDetails() {
      axios
        .get(api.ACTIONS.BOOKINGS + "/" + this.$route.params.id)
        .then((res) => {
          this.data = res.data.data;
          // reset routes on each load
          this.path = [];
          this.approachPath = [];
          this.estimatedPath = [];
          this.captainEstimatedPath = [];

          this.complaintStatus = this.data.meta.complaint_status;
          this.complaintSelectedVal = this.data.all_complaints.length > 0 ? this.data.all_complaints[0].category_id : null;
          document.title = this.$t("ridePage.docTitle", { id: this.data.id });
          
          // Fetch booking complaints
          this.fetchComplaints();

          // Fill estimated route from backend (summaries.estimated_route decoded)
          if (this.data.estimated_route && Array.isArray(this.data.estimated_route)) {
            this.estimatedPath = this.data.estimated_route.map((p) => ({
              lat: parseFloat(p.lat),
              lng: parseFloat(p.lng),
            }));
          }
          // Fill estimated approach route (driver -> pickup) from backend
          if (
            this.data.captain_passenger_estimated_route &&
            Array.isArray(this.data.captain_passenger_estimated_route)
          ) {
            this.captainEstimatedPath = this.data.captain_passenger_estimated_route.map((p) => ({
              lat: parseFloat(p.lat),
              lng: parseFloat(p.lng),
            }));
          }
          try {
            this.driverLocation.lat =
              this.data.captain_last_location[0].location.coordinates[1];
            this.driverLocation.lng =
              this.data.captain_last_location[0].location.coordinates[0];
          } catch (err) {
            // do nothing
          }
          // Driven-so-far route: terminal rides for the audit view, ongoing
          // ones so the approach/trip segments exist before live points arrive
          if (
            [
              "STARTED",
              "ARRIVED",
              "PICKED_UP",
              "DONE",
              "PAID",
              "REJECTED",
              "CANCELLED",
              "CAPTAIN_CANCELLED",
              "PAX_CANCELLED",
            ].includes(res.data.data.status)
          ) {
            this.fetchRideRoute();
          }
          if (this.startTracking) this.startLiveSnapshot();
          this.fitInitialView();
        })
        .catch((error) => {
          console.log("RidePage Error: ", error);
        });
    },
    // GET ride/{ride_id}/route: sanitized, sorted, phase-tagged server-side
    // (legacy rides only carry "trip" points). Replaces both live buffers.
    fetchRideRoute() {
      const rideId = this.data ? this.data.ride_id : null;
      if (!rideId) return;
      axios
        .get("ride/" + rideId + "/route")
        .then((res) => {
          if (this._destroyed || !this.data || this.data.ride_id !== rideId) return;
          const approach = [];
          const trip = [];
          (res.data.data.route || []).forEach((e) => {
            const point = {
              lat: parseFloat(e.lat),
              lng: parseFloat(e.lng),
            };
            if (e.phase === "approach") approach.push(point);
            else trip.push(point);
          });
          this.approachPath = approach;
          this.path = trip;
          // Newest persisted point seeds the car only while no live ping has
          // been applied yet AND nothing fresher is placed already: the Redis
          // captain_last_location seed (written on every ping) beats this
          // persisted point (written only on >= 2 m moves, up to a ping behind)
          const last = trip[trip.length - 1] || approach[approach.length - 1];
          if (
            last &&
            this.startTracking &&
            !this._lastLiveFixAt &&
            !this.hasDriverPosition
          ) {
            this.cancelDriverTween();
            this.driverLocation.lat = last.lat;
            this.driverLocation.lng = last.lng;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    // One-shot camera fit on load. Never re-run: afterwards the operator owns
    // the camera (followDriver is the opt-in pan-along mode).
    fitInitialView() {
      if (this._initialFitDone) return;
      this._initialFitDone = true;
      this.$nextTick(() => {
        if (!this.$refs.map) return;
        this.$refs.map.$mapPromise.then((map) => {
          zoomUI(map, this);
          const points = [
            this.pickupPosition,
            this.dropoffPosition,
            this.passengerRealPosition,
            this.startTracking && this.hasDriverPosition
              ? { lat: this.driverLocation.lat, lng: this.driverLocation.lng }
              : null,
          ].filter(Boolean);
          const seen = {};
          const distinct = points.filter((p) => {
            const key = p.lat + "," + p.lng;
            if (seen[key]) return false;
            seen[key] = true;
            return true;
          });
          if (distinct.length >= 2) {
            const bounds = new google.maps.LatLngBounds();
            distinct.forEach((p) => bounds.extend(p));
            // maxZoom clamp instead of a once-'idle' setZoom: two close points
            // would otherwise land on max zoom, and the idle listener used to
            // fire on the operator's first manual pan whenever the fit no-oped
            map.setOptions({ maxZoom: 17 });
            map.fitBounds(bounds, 48);
            map.setOptions({ maxZoom: null });
          } else if (distinct.length === 1) {
            map.setCenter(distinct[0]);
            map.setZoom(16);
          }
        });
      });
    },
    // users-namespace 'captain_location_updated': every captain's pings reach
    // every open page, so the id filter stays the first check
    updateDriverLocation(data) {
      const pos = data ? toLatLng(data.location) : null;
      if (!pos) return;
      if (
        !this.data ||
        !this.data.ride ||
        data.captain_id != this.data.ride.captain_id ||
        !this.startTracking
      )
        return;
      this._lastLiveFixAt = Date.now();
      this.applyLivePosition(pos);
    },
    // Shared by the socket feed and the snapshot poll: extend the live
    // polyline, glide the car, optionally pan. Never setZoom / fitBounds.
    applyLivePosition(pos) {
      this.appendLivePoint(pos);
      this.animateDriverTo(pos);
      if (this.followDriver) this.panMapTo(pos);
    },
    panMapTo(pos) {
      if (!this.$refs.map) return;
      this.$refs.map.$mapPromise.then((map) => map.panTo(pos));
    },
    appendLivePoint(pos) {
      const phase = this.livePhase;
      if (!phase) return;
      const target = phase === "approach" ? this.approachPath : this.path;
      const last = target[target.length - 1];
      // Parked heartbeats repeat the same fix every 15 s
      if (last && last.lat === pos.lat && last.lng === pos.lng) return;
      target.push({ lat: pos.lat, lng: pos.lng });
    },
    // Glide the car between pings. Per-frame positions go straight to the
    // marker object: no reactive write per frame, so no page re-render (the
    // inline :icon literals on the other markers would call setIcon on every
    // render). The reactive driverLocation is committed once, at the end.
    animateDriverTo(pos) {
      this.cancelDriverTween();
      const marker = this.$refs.driverMarker
        ? this.$refs.driverMarker.$markerObject
        : null;
      const current = marker ? marker.getPosition() : null;
      if (!current) {
        // No marker on the map yet (first fix, tracking just resumed): place it
        this.driverLocation.lat = pos.lat;
        this.driverLocation.lng = pos.lng;
        return;
      }
      const from = { lat: current.lat(), lng: current.lng() };
      let start = null;
      const step = (now) => {
        if (start === null) start = now;
        const t = Math.min((now - start) / DRIVER_TWEEN_MS, 1);
        marker.setPosition({
          lat: from.lat + (pos.lat - from.lat) * t,
          lng: from.lng + (pos.lng - from.lng) * t,
        });
        if (t < 1) {
          this._driverTween = window.requestAnimationFrame(step);
          return;
        }
        this._driverTween = null;
        this.driverLocation.lat = pos.lat;
        this.driverLocation.lng = pos.lng;
      };
      this._driverTween = window.requestAnimationFrame(step);
    },
    cancelDriverTween() {
      if (!this._driverTween) return;
      window.cancelAnimationFrame(this._driverTween);
      this._driverTween = null;
    },
    // Zombie-socket / reconnect safety net: re-read ONLY the live fields
    // (status, ride, ride_id, captain_last_location). Never touches the
    // paths and never moves the camera.
    refreshLiveSnapshot() {
      if (this._destroyed || !this.data || this._snapshotInFlight) return;
      this._snapshotInFlight = true;
      // A socket status event that lands while this request is in flight is
      // fresher than the reply (built from the older DB state): never let the
      // snapshot roll data.status back behind it
      const sentAt = Date.now();
      axios
        .get(api.ACTIONS.BOOKINGS + "/" + this.$route.params.id)
        .then((res) => {
          if (this._destroyed) return;
          const fresh = res.data.data;
          if (!fresh || !this.data || fresh.id !== this.data.id) return;
          const prevStatus = this.data.status;
          if (fresh.ride) this.data.ride = fresh.ride;
          if (fresh.ride_id) this.data.ride_id = fresh.ride_id;
          this.data.captain_last_location = fresh.captain_last_location;
          if (sentAt > this._lastStatusEventAt) {
            this.data.status = fresh.status;
            if (fresh.status !== prevStatus) {
              this.fetchStatuses();
              this.syncLiveState(fresh.status);
            }
          }
          // Position from the snapshot only when the socket has been quiet
          // for a whole poll interval (a healthy feed is always fresher)
          const last = Array.isArray(fresh.captain_last_location)
            ? fresh.captain_last_location[0]
            : null;
          const pos = last ? toLatLng(last.location) : null;
          if (
            pos &&
            pos.lat &&
            pos.lng &&
            this.startTracking &&
            Date.now() - this._lastLiveFixAt > LIVE_SNAPSHOT_MS
          )
            this.applyLivePosition(pos);
        })
        .catch((err) => {
          console.log("RidePage snapshot error: ", err);
        })
        .finally(() => {
          this._snapshotInFlight = false;
        });
    },
    startLiveSnapshot() {
      if (this._destroyed || this._snapshotTimer) return;
      this._snapshotTimer = setInterval(this.refreshLiveSnapshot, LIVE_SNAPSHOT_MS);
    },
    stopLiveSnapshot() {
      if (!this._snapshotTimer) return;
      clearInterval(this._snapshotTimer);
      this._snapshotTimer = null;
    },
    // After data.status changed (socket event or snapshot): keep the live
    // helpers in step with the new phase
    syncLiveState(newStatus) {
      // The server only persists tracking from STARTED (approach) on: drop
      // whatever a reservation's ACCEPTED window buffered before that
      if (newStatus === "STARTED") this.approachPath = [];
      // Trip polyline starts where the car is at pickup
      if (newStatus === "PICKED_UP" && this.hasDriverPosition)
        this.appendLivePoint({
          lat: this.driverLocation.lat,
          lng: this.driverLocation.lng,
        });
      // Sanitized server route replaces the raw live buffers
      if (["DONE", "PAID"].includes(newStatus)) this.fetchRideRoute();
      if (this.startTracking) {
        this.startLiveSnapshot();
      } else {
        this.stopLiveSnapshot();
        this.cancelDriverTween();
      }
    },
    fetchStatuses() {
      axios
        .get(`booking/${this.data.id}/statuses`)
        .then((res) => {
          this.data.statuses = res.data.data;
        })
        .catch((err) => {
          console.log("RidePage statuses error: ", err);
        });
    },
    onSocketDisconnect() {
      this.liveFeedDisconnected = true;
    },
    onSocketError(err) {
      // Namespace auth failure / connection error: the feed is dead even
      // though `disconnect` never fires. The snapshot poll covers the gap.
      console.warn("[RidePage] live feed error", err);
      this.liveFeedDisconnected = true;
    },
    onSocketConnect() {
      if (!this.liveFeedDisconnected) return;
      this.liveFeedDisconnected = false;
      // Anything can have changed while the feed was down
      if (this.startTracking) this.refreshLiveSnapshot();
    },
    dashedLineOptions(color) {
      // vue2-google-maps dashed-line trick: invisible stroke + repeated dash icon
      return {
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: "M 0,-1 0,1",
              strokeOpacity: 1,
              strokeColor: color,
              strokeWeight: 3,
              scale: 3,
            },
            offset: "0",
            repeat: "14px",
          },
        ],
      };
    },
    formSvg(color) {
      return (
        "data:image/svg+xml;utf8," +
        encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="33.625" height="41.385" viewBox="0 0 33.625 41.385">
        <defs>
          <clipPath id="clip-path">
            <rect id="Rectangle_3619" data-name="Rectangle 3619" width="33.625" height="41.385" transform="translate(163 565)" fill="none"/>
          </clipPath>
        </defs>
        <g id="Mask_Group_6" data-name="Mask Group 6" transform="translate(-163 -565)" clip-path="url(#clip-path)">
          <g id="Monaco_Pin_Active" data-name="Monaco Pin Active" transform="translate(163.259 565)">
            <g id="Group_322" data-name="Group 322" transform="translate(6.622 22.284)">
              <path id="Path_374" data-name="Path 374" d="M42.018,34.462l-9.932,19.1-9.932-19.1a17.086,17.086,0,0,0,19.865,0Z" transform="translate(-22.154 -34.462)" fill="${color}"/>
            </g>
            <g id="Group_323" data-name="Group 323" transform="translate(0)">
              <circle id="Ellipse_3825" data-name="Ellipse 3825" cx="16" cy="16" r="16" transform="translate(0.741)" fill="${color}"/>
              <path id="Path_376" data-name="Path 376" d="M23.081,19.3a9.705,9.705,0,0,1-5.418-1.658l-.123-.118a8.642,8.642,0,0,1-3.694-7.1,9.242,9.242,0,0,1,18.47,0,8.642,8.642,0,0,1-3.694,7.1l-.123.118A9.705,9.705,0,0,1,23.081,19.3Z" transform="translate(-6.527 5.499)" fill="#fff"/>
            </g>
          </g>
        </g>
      </svg>`)
      );
    },
    getLogs() {
      axios
        .get(`${api.ACTIONS.LOGS}/${this.$route.params.id}`)
        .then((res) => {
          this.logs = res.data.data.data;
        })
        .catch((err) => { });
    },
    addComplaint() {
      this.$dialova({
        title: this.$t("ridePage.enterComplaintTitle"),
        input: this.$t("ridePage.enterComplaint"),
        loader: true,
        fileUpload: this.$t("ridePage.soundFiles")
        // dropdown: {
        //   options: this.complaintCategories,
        //   placeholder: 'Select a category',
        //   name: 'complaint_type'
        // }
      }).then(({ value, close, files, dropdown }) => {
        const params = new FormData();
        if (value)
          params.append("description", value);
        params.append("booking_id", this.$route.params.id);
        //params.append("category_id", dropdown);
        if (files) {
          console.log(files.length);
          for (let i = 0; i < files.length; i++)
            params.append("wav_files[]", files[i]);
        }
        axios
          .post(`complaint/booking/${this.$route.params.id}/create`, params)
          .then(() => {
            window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.saved"));
            this.getOrderDetails();
            close();
          })
          .catch((err) => {
            console.log("err");
            console.log(err.response.data.message);
            window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
            close();
          });
        // .finally(() => close());
      });
    },
    saveCategory() {
      if (!this.complaintSelectedVal) {
        window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.chooseCategory"), "red");
        return;
      }
      this.loading = true;
      this.axios
        .post(`complaint/category`, {
          category_id: this.complaintSelectedVal,
          booking_id: this.$route.params.id
        })
        .then((res) => {
          this.loading = false;
        })
        .catch((err) => {
         // console.log(err.message);
          this.loading = false;
           window.getApp.$emit("SHOW_TOAST", err.response.data.message, "red");
        });
    },
    showDetails(complaint, id) {
      const audioPromise = axios.get(`complaint/${id}/audio`);
      const commentPromise = axios.get(`complaint_comments/${id}/get_comments`);
      Promise.allSettled([audioPromise, commentPromise]).then(([audios, comments]) => {
        complaint.audios = audios.value.data.data;
        complaint.comments = comments.value.data.data;
        complaint.comments.forEach(comment => {
          comment.audios = comment.audios.map((item) => {
            return {
              item: item,
              loading: false,
              urlObject: ''
            }
          });
        });
        // this.$complaintDialog({
        //   complaint: complaint,
        //   hideCancel: true,
        //   fileUpload: "Sound files",
        //   list: audios.value.data.data,
        //   additionalData: {
        //     id: id
        //   }
        // })
      });

      // axios.get(`complaint/${id}/audio`).then((res) => {
      //   this.$complaintDialog({
      //     complaint:complaint,
      //     hideCancel: true,
      //     list:audios,
      //     additionalData: {
      //       id: id
      //     }
      //   })
      //     .then(() => {})
      //     .catch(() => {});
      // });
    },
    updateStatus(id, newStatus, oldStatus, driver, rideId) {
      if (id === this.data.id) {
        // Ordering stamp for refreshLiveSnapshot: a reply sent before this
        // event carries an older status and must not override it
        this._lastStatusEventAt = Date.now();
        this.data.status = newStatus;
        if (driver) {
          this.data["captain_first_name"] = driver.first_name;
          this.data["captain_last_name"] = driver.last_name;
          this.data["taxi_number"] = driver.username;
          this.data['car_brand_model'] = driver.car_brand_model;
          this.data['car_brand'] = driver.car_brand;
          this.data['car_color'] = driver.car_color;
          this.data['car_plate_number'] = driver.car_plate_number;
          this.data["ride"] = {};
          this.data.ride["captain_id"] = driver.id;
        }
        if (rideId) {
          this.data["ride_id"] = rideId;
        }
        this.fetchStatuses();
        this.syncLiveState(newStatus);
      }
    },
    focusOnStatus(log) {
      const position = log && log.location ? toLatLng(log.location) : null;
      if (!position) {
        window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.noLocationForStatus"), "orange");
        return;
      }

      // Temporary highlight so the focused event stands out on the map
      if (this.highlightTimeoutId) clearTimeout(this.highlightTimeoutId);
      this.highlightPos = position;
      this.highlightTimeoutId = setTimeout(() => {
        this.highlightPos = null;
        this.highlightTimeoutId = null;
      }, 6000);

      window.requestAnimationFrame(() => {
        this.$refs.map.$mapPromise.then((map) => {
          map.setCenter(position);
          map.setZoom(18);
        });
      });
    },
    getMarkerPositionWithOffset(marker, index) {
      const basePosition = marker._originalPosition || marker._position;
      if (!basePosition) return marker._position;
      
      // Check if there are other markers at the same location
      const sameLocationMarkers = this.statusMarkers.filter((m, idx) => {
        if (!m._originalPosition) return false;
        const origPos = m._originalPosition;
        const distance = Math.sqrt(
          Math.pow(origPos.lat - basePosition.lat, 2) + 
          Math.pow(origPos.lng - basePosition.lng, 2)
        );
        return distance < 0.0001; // Very close markers (same location)
      });
      
      // Only add offset if there are multiple markers at same location
      if (sameLocationMarkers.length <= 1) {
        return basePosition;
      }
      
      // Find the index of this marker among same-location markers
      let sameLocationIndex = -1;
      for (let i = 0; i < sameLocationMarkers.length; i++) {
        const m = sameLocationMarkers[i];
        if (m._originalPosition && 
            Math.abs(m._originalPosition.lat - basePosition.lat) < 0.0001 &&
            Math.abs(m._originalPosition.lng - basePosition.lng) < 0.0001) {
          // Check if it's the same marker by comparing status and created_at
          if (m.status === marker.status && m.created_at === marker.created_at) {
            sameLocationIndex = i;
            break;
          }
        }
      }
      
      // If not found by exact match, use index in statusMarkers array
      if (sameLocationIndex === -1) {
        sameLocationIndex = sameLocationMarkers.findIndex(m => 
          m._originalPosition && 
          Math.abs(m._originalPosition.lat - basePosition.lat) < 0.0001 &&
          Math.abs(m._originalPosition.lng - basePosition.lng) < 0.0001
        );
      }
      
      // Calculate offset using golden angle spiral for even distribution
      const offset = 0.0001; // Increased offset in degrees (~10 meters)
      const goldenAngle = 137.508; // Golden angle in degrees
      const angle = (sameLocationIndex * goldenAngle) % 360;
      const radius = sameLocationIndex * offset * 0.5;
      
      return {
        lat: basePosition.lat + radius * Math.cos(angle * Math.PI / 180),
        lng: basePosition.lng + radius * Math.sin(angle * Math.PI / 180),
      };
    },
    getStatusLabel(status) {
      const labels = {
        PENDING: this.$t("ridePage.statusPending"),
        ACCEPTED: this.$t("ridePage.statusAccepted"),
        STARTED: this.$t("ridePage.statusStarted"),
        ARRIVED: this.$t("ridePage.statusArrived"),
        PICKED_UP: this.$t("ridePage.statusPickedUp"),
        DONE: this.$t("ridePage.statusDone"),
        PAID: this.$t("ridePage.statusPaid"),
        SYSTEM_CANCELLED: this.$t("ridePage.statusSystemCancelled"),
        PASSENGER_CANCELLED: this.$t("ridePage.statusPassengerCancelled"),
        CAPTAIN_CANCELLED: this.$t("ridePage.statusCaptainCancelled"),
        CC_CANCELLED: this.$t("ridePage.statusCcCancelled"),
      };
      return labels[status] || status;
    },
    complaintStatusLabel(status) {
      const labels = {
        PENDING: this.$t("ridePage.complaintPending"),
        RESOLVED: this.$t("ridePage.complaintResolved"),
      };
      return labels[status] || status;
    },
    // Booking Complaints Methods
    fetchComplaints() {
      if (!this.data || !this.data.id) return;
      
      axios
        .get(`${api.ACTIONS.BOOKINGS}/${this.data.id}/complaints`)
        .then((res) => {
          this.complaints = res.data.data.data || [];
        })
        .catch((err) => {
          console.error("Error fetching complaints:", err);
          window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.failedLoadComplaints"), "red");
        });
    },
    settleUnpaid() {
      if (!this.data || !this.data.id || this.settlingUnpaid) return;
      this.settlingUnpaid = true;
      axios
        .post(api.ACTIONS.SETTLE_UNPAID_BOOKING(this.data.id))
        .then((res) => {
          const payload = (res.data && res.data.data) || {};
          const entry = payload.journal_entry;
          window.getApp.$emit(
            "SHOW_TOAST",
            entry && entry.reference_no
              ? this.$t("ridePage.settledUnpaidWithEntry", { ref: entry.reference_no })
              : this.$t("ridePage.settledUnpaid")
          );
          this.unpaidDialog = false;
          this.refreshLiveSnapshot();
        })
        .catch((err) => {
          const message = (err.response && err.response.data && err.response.data.message) || this.$t("ridePage.settleUnpaidFailed");
          window.getApp.$emit("SHOW_TOAST", message, "red");
        })
        .finally(() => {
          this.settlingUnpaid = false;
        });
    },
    createComplaint() {
      if (!this.newComplaintDescription || this.newComplaintDescription.trim() === '') {
        window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.enterComplaintDescription"), "red");
        return;
      }
      
      if (!this.data || !this.data.id) return;
      
      this.creatingComplaint = true;
      
      axios
        .post(`${api.ACTIONS.BOOKINGS}/${this.data.id}/complaints`, {
          description: this.newComplaintDescription.trim()
        })
        .then((res) => {
          window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.complaintCreated"));
          this.newComplaintDescription = "";
          this.fetchComplaints();
        })
        .catch((err) => {
          console.error("Error creating complaint:", err);
          const message = (err.response && err.response.data && err.response.data.message) || this.$t("ridePage.failedCreateComplaint");
          window.getApp.$emit("SHOW_TOAST", message, "red");
        })
        .finally(() => {
          this.creatingComplaint = false;
        });
    },
    openResolveDialog(complaint) {
      this.selectedComplaint = complaint;
      this.resolutionComment = "";
      this.resolveDialog = true;
    },
    resolveComplaint() {
      if (!this.selectedComplaint) return;
      
      this.resolvingComplaint = true;
      
      axios
        .put(`booking-complaints/${this.selectedComplaint.id}/resolve`, {
          resolution_comment: this.resolutionComment.trim() || null
        })
        .then((res) => {
          window.getApp.$emit("SHOW_TOAST", this.$t("ridePage.complaintResolvedToast"));
          this.resolveDialog = false;
          this.selectedComplaint = null;
          this.resolutionComment = "";
          this.fetchComplaints();
        })
        .catch((err) => {
          console.error("Error resolving complaint:", err);
          const message = (err.response && err.response.data && err.response.data.message) || this.$t("ridePage.failedResolveComplaint");
          window.getApp.$emit("SHOW_TOAST", message, "red");
        })
        .finally(() => {
          this.resolvingComplaint = false;
        });
    },
    // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
    formatDate(date) {
      return formatCompanyTime(date, "DD/MM/YYYY HH:mm");
    },
  },
  beforeDestroy() {
    // In-flight snapshot / route replies must not touch the dead instance
    // (a status change in a late reply used to re-arm the poll interval)
    this._destroyed = true;
    if (this.highlightTimeoutId) clearTimeout(this.highlightTimeoutId);
    this.cancelDriverTween();
    this.stopLiveSnapshot();
  },
  created() {
    // Non-reactive working state, deliberately NOT in data(): none of it is
    // rendered and Vue must not observe it.
    this._initialFitDone = false;
    this._driverTween = null; // requestAnimationFrame handle of the running car tween
    this._snapshotTimer = null;
    this._snapshotInFlight = false;
    this._lastLiveFixAt = 0; // Date.now() of the last accepted socket ping
    this._lastStatusEventAt = 0; // Date.now() of the last socket status event
    this._destroyed = false; // set in beforeDestroy: in-flight callbacks bail out
    this.getOrderDetails();
  // this.getLogs(); // Disabled temporarily (no need to hit /api/user/log/{id} for now)
    // console.log(this.$store.getters.getBookingsUpdates);
    // this.$store.dispatch("listenBookingUpdate", this.$route.params.id || 'x');
  },
  mounted() {
    // A socket that is already down never fires `disconnect` for this page
    if (this.socket && this.socket.disconnected) this.liveFeedDisconnected = true;
  // Temporarily disable complaints categories fetching (no need to hit /api/user/complaint-category for now)
  // this.loading = true;
  // this.$store
  //   .dispatch("fetchComplaintCategories")
  //   .then((data) => {
  //     this.complaintCategories = data;
  //     this.categoryDialog = true;
  //     this.loading = false;
  //   })
  //   .catch((err) => {
  //     console.error(err);
  //     this.loading = false;
  //   });
  }
};
</script>

<style lang="scss">
.complaint-button {
  padding: 5px;
  width: 100%;
  background: #27a432;
  color: white;
}

.complaints-container {
  width: 100%;
  height: auto;
  float: inline-start;
  padding: 1rem 1.25rem;
  background: white;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0px 0px 6px rgba($color: #000000, $alpha: 0.08);
  overflow-y: overlay;
}

.complaints-section {
  width: 100%;
  margin-top: 12px;
  
  .complaints-header {
    background-color: #f5f5f5;
    padding: 12px 16px;
  }
  
  .add-complaint-form {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 16px;
  }
  
  .complaints-list {
    max-height: 400px;
    overflow-y: auto;
  }
  
  .complaint-item {
    .complaint-header-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 8px;
    }
    
    .complaint-date {
      font-size: 12px;
      color: #666;
    }
    
    .complaint-description {
      color: #333;
      white-space: pre-wrap;
      word-break: break-word;
    }
    
    .resolved-info {
      background-color: #f0f8f0;
      padding: 12px;
      border-radius: 4px;
      margin-top: 8px;
      
      .resolved-date {
        font-size: 12px;
        color: #666;
      }
      
      .resolution-comment {
        margin-top: 8px;
        font-size: 13px;
        color: #333;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }
  }
  
  .no-complaints {
    color: #999;
  }
}

.card-container {
  width: 430px;
  height: auto;
  position: absolute;
  z-index: 4;
  bottom: 0px;
  inset-inline-start: 55px;
  max-height: calc(100vh - 64px);
  padding-top: 22px;
  padding-bottom: 22px;
  overflow-y: overlay;

  &::-webkit-scrollbar {
    width: 0;
  }

  .scroller {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    flex-direction: column;
    gap: 12px;

    div {
      flex-grow: 0;
      flex-shrink: 1;
    }
  }
}

.map {
  height: calc(100vh - 64px);
  outline: none;
}

.map-overlays {
  position: absolute;
  z-index: 4;
  top: 22px;
  // Top corner of the visible map, right beside the details card column
  // (card = 55px inline offset + 430px width); RTL puts it top-right.
  inset-inline-start: 505px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.map-follow-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 0px 6px rgba($color: #000000, $alpha: 0.08);
  font-size: 12px;
  color: #222629;
  cursor: pointer;
  user-select: none;

  input {
    margin: 0;
    cursor: pointer;
  }
}

.ride-live-feed-banner {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  background: #e53935;
  color: white;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.map-legend {
  padding: 10px 14px;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 0px 6px rgba($color: #000000, $alpha: 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;

  .legend-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .legend-swatch {
    width: 26px;
    height: 4px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .legend-swatch--dashed {
    height: 0;
    border-radius: 0;
  }

  .legend-label {
    font-size: 12px;
    color: #222629;
  }
}

.zoom-container {
  width: max-content;
  height: max-content;
  bottom: 20px !important;
  inset-inline-end: 55px !important;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.zoom-button {
  height: 48px;
  width: 48px;
  background-color: #fff;
  border: 1px solid #c1c5d8;
  color: #222629;
  font-size: 18px;
  border-radius: 10px;
  outline: none;
}

.geofence-logs {
  color: #222629;

  tr,
  td,
  th {
    padding: 2px;
  }

  tr {
    td:first-child {
      padding-inline-start: 0;
    }
  }
}
</style>
